// NPC System - Turkish Mythology ARPG
// NPCs for hub zones - dialog, shops, quests

import { Renderer } from '@client/core/Renderer';
import { Camera } from '@client/core/Camera';
import { LPCSpriteRenderer, LPCSpriteLayer } from '@client/core/LPCSpriteRenderer';
import { AssetLoader } from '@client/core/AssetLoader';
import { t } from '@locales/index';

export type NPCType =
  | 'kam'           // Shaman - Main quest giver
  | 'demirci'       // Blacksmith - Crafting/Enhancement
  | 'tacir'         // Merchant - Buy/Sell items
  | 'ihtiyar'       // Elder - Lore/Tips
  | 'otaci'         // Herbalist - Potions
  | 'muhafiz';      // Guard - Zone info

export type QuestDialogAction =
  | { type: 'accept_quest'; questId: string }
  | { type: 'complete_quest'; questId: string };

export interface DialogOption {
  text: string;
  action?: () => void;
  nextDialogId?: string;
  requiresLevel?: number;
  requiresItem?: string;
  questAction?: QuestDialogAction;
}

export interface Dialog {
  id: string;
  speaker: string;
  text: string;
  options?: DialogOption[];
}

export interface NPCConfig {
  id: string;
  name: string;
  title: string;
  type: NPCType;
  position: { x: number; y: number };
  dialogs: Dialog[];
  shopItems?: string[];  // Item IDs for shop NPCs
}

/** NPC type → LPC layers mapping */
const NPC_LPC_LAYERS: Record<NPCType, LPCSpriteLayer[]> = {
  kam: [
    { imageKey: 'lpc_body_male' },
    { imageKey: 'lpc_legs_pants' },
    { imageKey: 'lpc_torso_robe', tint: '#4B2D73', tintAlpha: 0.5 },
    { imageKey: 'lpc_hair_brown', tint: '#1A1A2E', tintAlpha: 0.5 },
    { imageKey: 'lpc_weapon_staff' },
  ],
  demirci: [
    { imageKey: 'lpc_body_male' },
    { imageKey: 'lpc_legs_pants' },
    { imageKey: 'lpc_boots_brown' },
    { imageKey: 'lpc_torso_leather' },
    { imageKey: 'lpc_torso_leather_shoulders' },
    { imageKey: 'lpc_gloves_leather' },
    { imageKey: 'lpc_hair_brown' },
  ],
  tacir: [
    { imageKey: 'lpc_body_male' },
    { imageKey: 'lpc_legs_pants' },
    { imageKey: 'lpc_boots_brown' },
    { imageKey: 'lpc_belt' },
    { imageKey: 'lpc_torso_chain', tint: '#DAA520', tintAlpha: 0.35 },
    { imageKey: 'lpc_hair_brown' },
  ],
  ihtiyar: [
    { imageKey: 'lpc_body_male' },
    { imageKey: 'lpc_legs_pants' },
    { imageKey: 'lpc_torso_robe', tint: '#CCCCCC', tintAlpha: 0.4 },
    { imageKey: 'lpc_hair_brown', tint: '#C0C0C0', tintAlpha: 0.6 },
    { imageKey: 'lpc_weapon_staff' },
  ],
  otaci: [
    { imageKey: 'lpc_body_male' },
    { imageKey: 'lpc_legs_pants' },
    { imageKey: 'lpc_boots_brown' },
    { imageKey: 'lpc_torso_robe', tint: '#2E8B57', tintAlpha: 0.45 },
    { imageKey: 'lpc_hair_brown' },
  ],
  muhafiz: [
    { imageKey: 'lpc_body_male' },
    { imageKey: 'lpc_legs_pants' },
    { imageKey: 'lpc_boots_brown' },
    { imageKey: 'lpc_torso_plate' },
    { imageKey: 'lpc_torso_plate_arms' },
    { imageKey: 'lpc_gloves_metal' },
    { imageKey: 'lpc_helm_metal' },
    { imageKey: 'lpc_weapon_longsword' },
    { imageKey: 'lpc_shield' },
  ],
};

export class NPC {
  // Shared LPC renderer (set once via static init)
  private static _lpcRenderer: LPCSpriteRenderer | null = null;

  static initLPCRenderer(assetLoader: AssetLoader): void {
    if (!NPC._lpcRenderer) {
      NPC._lpcRenderer = new LPCSpriteRenderer(assetLoader);
    }
  }

  public id: string;
  public name: string;
  public title: string;
  public type: NPCType;
  public position: { x: number; y: number };
  public dialogs: Dialog[];
  public shopItems: string[];

  // Visual
  private width: number = 32;
  private height: number = 48;
  private color: string;
  private icon: string;
  private lpcLayers: LPCSpriteLayer[];

  // Animation
  private idlePhase: number = 0;
  private interactionRange: number = 60;

  // State
  private isHighlighted: boolean = false;

  // Quest marker
  public questMarkerType: 'available' | 'active' | 'completable' | 'none' = 'none';
  private questMarkerBob: number = 0;

  constructor(config: NPCConfig) {
    this.id = config.id;
    this.name = config.name;
    this.title = config.title;
    this.type = config.type;
    this.position = { ...config.position };
    this.dialogs = config.dialogs;
    this.shopItems = config.shopItems || [];
    this.lpcLayers = NPC_LPC_LAYERS[this.type] || NPC_LPC_LAYERS.muhafiz;

    // Set visual based on type
    switch (this.type) {
      case 'kam':
        this.color = '#9b59b6';  // Purple for shaman
        this.icon = 'K';
        break;
      case 'demirci':
        this.color = '#e67e22';  // Orange for blacksmith
        this.icon = 'D';
        break;
      case 'tacir':
        this.color = '#f1c40f';  // Gold for merchant
        this.icon = 'T';
        break;
      case 'ihtiyar':
        this.color = '#ecf0f1';  // White for elder
        this.icon = 'I';
        break;
      case 'otaci':
        this.color = '#2ecc71';  // Green for herbalist
        this.icon = 'O';
        break;
      case 'muhafiz':
        this.color = '#3498db';  // Blue for guard
        this.icon = 'M';
        break;
    }
  }

  update(deltaTime: number): void {
    this.idlePhase += deltaTime * 2;
    this.questMarkerBob += deltaTime * 3;
  }

  setHighlighted(highlighted: boolean): void {
    this.isHighlighted = highlighted;
  }

  isInRange(x: number, y: number): boolean {
    const dx = x - this.position.x;
    const dy = y - this.position.y;
    return Math.sqrt(dx * dx + dy * dy) < this.interactionRange;
  }

  getDialog(dialogId?: string): Dialog | null {
    if (dialogId) {
      return this.dialogs.find(d => d.id === dialogId) || null;
    }
    return this.dialogs[0] || null;
  }

  /**
   * Build a dynamic quest dialog for this NPC based on quest state.
   * Returns a Dialog with quest-specific options, or null if no quest actions available.
   * The caller (Game.ts) should overlay these options onto the NPC's greeting dialog.
   */
  buildQuestDialog(
    availableQuests: Array<{ id: string; name: string; startDialog?: string }>,
    completableQuests: Array<{ id: string; name: string; completeDialog?: string }>,
    activeQuests: Array<{ id: string; name: string; progressDialog?: string }>,
  ): Dialog | null {
    const options: DialogOption[] = [];

    // Completable quests first (highest priority)
    for (const quest of completableQuests) {
      options.push({
        text: `${t('questLog.completeQuest')}: ${quest.name}`,
        questAction: { type: 'complete_quest', questId: quest.id },
      });
    }

    // Available quests
    for (const quest of availableQuests) {
      options.push({
        text: `Gorevi Kabul Et: ${quest.name}`,
        questAction: { type: 'accept_quest', questId: quest.id },
      });
    }

    // If there are quest options, also add a dismiss option
    if (options.length > 0) {
      options.push({ text: 'Belki sonra.' });
    }

    // Active quests show progress info as additional dialog options
    for (const quest of activeQuests) {
      options.push({
        text: `Gorev Durumu: ${quest.name}`,
        nextDialogId: undefined, // Just closes dialog after showing progress
      });
    }

    if (options.length === 0) return null;

    // Determine dialog text
    let dialogText = '';
    if (completableQuests.length > 0) {
      const q = completableQuests[0];
      dialogText = q.completeDialog || `Aferin sana! "${q.name}" gorevi tamamlandi. Odullerini al.`;
    } else if (availableQuests.length > 0) {
      const q = availableQuests[0];
      dialogText = q.startDialog || `Sana bir gorev vermek istiyorum: "${q.name}".`;
    } else if (activeQuests.length > 0) {
      const q = activeQuests[0];
      dialogText = q.progressDialog || `"${q.name}" gorevi devam ediyor. Hedeflerini tamamla.`;
    }

    return {
      id: '__quest_dialog__',
      speaker: this.name,
      text: dialogText,
      options,
    };
  }

  render(renderer: Renderer, camera: Camera): void {
    const ctx = renderer.context;
    const screenPos = camera.worldToScreen(this.position.x, this.position.y);
    const z = camera.zoom;

    // Idle: no bobbing
    const bob = 0;

    ctx.save();

    // Shadow
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y + 2 * z, 16 * z, 6 * z, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fill();

    // === LPC SPRITE (preferred) ===
    let usedLPC = false;
    if (NPC._lpcRenderer && NPC._lpcRenderer.areLayersLoaded(this.lpcLayers)) {
      ctx.imageSmoothingEnabled = false;
      const spriteScale = 1.6;
      const spriteW = 64 * z * spriteScale;
      const spriteH = 64 * z * spriteScale;
      const idleFrame = 0;
      usedLPC = NPC._lpcRenderer.drawComposite(
        ctx, this.lpcLayers, 'idle', 'down', idleFrame,
        screenPos.x, screenPos.y, spriteW, spriteH
      );
    }

    // === PROCEDURAL FALLBACK ===
    if (!usedLPC) {
      // Body
      ctx.fillStyle = this.color;
      ctx.fillRect(
        screenPos.x - this.width / 2,
        screenPos.y - this.height + bob,
        this.width,
        this.height
      );

      // Robe/clothing overlay
      ctx.fillStyle = this.darkenColor(this.color, 30);
      ctx.fillRect(
        screenPos.x - this.width / 2,
        screenPos.y - this.height / 2 + bob,
        this.width,
        this.height / 2
      );

      // Head
      ctx.beginPath();
      ctx.arc(
        screenPos.x,
        screenPos.y - this.height - 8 + bob,
        12,
        0,
        Math.PI * 2
      );
      ctx.fillStyle = '#f5deb3';  // Skin tone
      ctx.fill();

      // Icon on chest
      ctx.font = 'bold 14px Arial';
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.fillText(this.icon, screenPos.x, screenPos.y - this.height / 2 + 5 + bob);
    }

    // Highlight effect when in range
    if (this.isHighlighted) {
      ctx.strokeStyle = '#ffd700';
      ctx.lineWidth = 2;
      ctx.strokeRect(
        screenPos.x - this.width / 2 - 4,
        screenPos.y - this.height - 24 + bob,
        this.width + 8,
        this.height + 30
      );

      // Interaction hint
      ctx.font = '10px Arial';
      ctx.fillStyle = '#ffd700';
      ctx.fillText('[Tikla] Konus', screenPos.x, screenPos.y + 20);
    }

    // Name and title above head
    ctx.font = 'bold 12px Arial';
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.shadowColor = '#000000';
    ctx.shadowBlur = 2;
    ctx.fillText(this.name, screenPos.x, screenPos.y - this.height - 25 + bob);

    ctx.font = '10px Arial';
    ctx.fillStyle = '#aaaaaa';
    ctx.fillText(this.title, screenPos.x, screenPos.y - this.height - 12 + bob);

    // Quest marker above name
    if (this.questMarkerType !== 'none') {
      const markerY = screenPos.y - this.height - 45 + bob + Math.sin(this.questMarkerBob) * 4;
      const markerX = screenPos.x;

      let marker = '';
      let markerColor = '';

      switch (this.questMarkerType) {
        case 'available':
          marker = '!';
          markerColor = '#ffd700'; // Gold - new quest available
          break;
        case 'completable':
          marker = '?';
          markerColor = '#ffd700'; // Gold - quest ready to turn in
          break;
        case 'active':
          marker = '?';
          markerColor = '#888888'; // Gray - quest in progress
          break;
      }

      // Glow effect
      ctx.shadowColor = markerColor;
      ctx.shadowBlur = 8;

      // Marker background circle
      ctx.beginPath();
      ctx.arc(markerX, markerY, 10, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fill();
      ctx.strokeStyle = markerColor;
      ctx.lineWidth = 2;
      ctx.stroke();

      // Marker symbol
      ctx.font = 'bold 16px Arial';
      ctx.fillStyle = markerColor;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(marker, markerX, markerY);
      ctx.textBaseline = 'alphabetic';

      ctx.shadowBlur = 0;
    }

    ctx.restore();
  }

  private darkenColor(color: string, amount: number): string {
    // Simple hex color darkening
    const hex = color.replace('#', '');
    const r = Math.max(0, parseInt(hex.substr(0, 2), 16) - amount);
    const g = Math.max(0, parseInt(hex.substr(2, 2), 16) - amount);
    const b = Math.max(0, parseInt(hex.substr(4, 2), 16) - amount);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }
}

// Predefined NPCs for Ruh Bataklığı (field NPCs)
export const RUH_BATAKLIGI_NPCS: NPCConfig[] = [
  {
    id: 'kayip_izci',
    name: 'Kayıp İzci',
    title: 'Yaralı Asker',
    type: 'muhafiz',
    position: { x: -512, y: 640 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Kayıp İzci',
        text: '*öksürük* S-savaşçı... Yaşıyorsun. Pusuya düştük... Bataklıktaki yaratıklar organize hareket ediyordu. Karabasanlar her yerdeydi.',
        options: [
          { text: 'Ne oldu?', nextDialogId: 'story' },
          { text: 'Sana nasıl yardım edebilirim?', nextDialogId: 'quest' }
        ]
      },
      {
        id: 'story',
        speaker: 'Kayıp İzci',
        text: 'Muhafız Börü bizi devriyeye gönderdi. Bataklığa girdiğimizde Karabasanlar bizi sardı. Sadece ben kurtuldum... Yaralarım ağır.',
        options: [
          { text: 'Sana nasıl yardım edebilirim?', nextDialogId: 'quest' }
        ]
      },
      {
        id: 'quest',
        speaker: 'Kayıp İzci',
        text: 'Ruh Damlalarına ihtiyacım var. Karabasanlardan düşer. 3 tane getirirsen yaralarımı iyileştirebilirim. Karşılığında sana değerli bir yüzük veririm.',
        options: [
          { text: 'Getireceğim!', nextDialogId: 'accept' },
          { text: 'Belki sonra.' }
        ]
      },
      {
        id: 'accept',
        speaker: 'Kayıp İzci',
        text: 'Tengri yardımcın olsun. Karabasanları öldürünce Ruh Damlası düşürürler. 3 tane yeterli.',
        options: [
          { text: 'Yolda.' }
        ]
      }
    ]
  },
  {
    id: 'kara_gulle',
    name: 'Kara Gülle',
    title: 'Deli Kam',
    type: 'kam',
    position: { x: 960, y: 1376 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Kara Gülle',
        text: '*kıkırdayarak* Hihihi... Bir ziyaretçi! Erlik\'in fısıltıları seni buraya getirdi, değil mi? Yoksa kendi ayakların mı? Hihihi...',
        options: [
          { text: 'Sen kimsin?', nextDialogId: 'who' },
          { text: 'Albastı Kraliçesi hakkında ne biliyorsun?', nextDialogId: 'boss_hint' },
          { text: 'Bir görevin var mı?', nextDialogId: 'quest' }
        ]
      },
      {
        id: 'who',
        speaker: 'Kara Gülle',
        text: '*gözleri parlayarak* Bir zamanlar köyün Kam\'ıydım... Sonra Erlik\'in sesi geldi. Dinledim. Çok dinledim. Şimdi başkaları duyamadığımı duyuyorum... *kıkırdar*',
        options: [
          { text: 'Albastı Kraliçesi hakkında ne biliyorsun?', nextDialogId: 'boss_hint' },
          { text: 'Bir görevin var mı?', nextDialogId: 'quest' }
        ]
      },
      {
        id: 'boss_hint',
        speaker: 'Kara Gülle',
        text: '*fısıldayarak* Kraliçe kanla güçlenir. Yakından vur. Çığlığını duyduğunda... taşların arkasına saklan. Kan yağmurunda... kenarlara koş. Hihihi... Basit, değil mi?',
        options: [
          { text: 'Teşekkürler, değerli bilgi.', nextDialogId: 'quest' },
          { text: 'Başka ipucu var mı?' , nextDialogId: 'hint2' }
        ]
      },
      {
        id: 'hint2',
        speaker: 'Kara Gülle',
        text: '*ciddi bir sesle* Tapınaktaki heykeller... onlar sadece süs değil. Sütunlar seni korur. Ama dikkat et, lav her şeyi yutar. *tekrar kıkırdar*',
        options: [
          { text: 'Anladım.' }
        ]
      },
      {
        id: 'quest',
        speaker: 'Kara Gülle',
        text: '*heyecanla* Ooo, bir bilmece ister misin? Kuzeydoğu adasındaki harabede bir idol var. Erlik İdolü! Getir bana, sana güçlü bir tılsım yapayım! *kıkırdayarak dans eder*',
        options: [
          { text: 'İdolü getireceğim.', nextDialogId: 'quest_accept' },
          { text: 'Belki sonra.' }
        ]
      },
      {
        id: 'quest_accept',
        speaker: 'Kara Gülle',
        text: 'Hihihi! Kuzeydoğu adası... taş zemin, harabeler... İdol orada bekliyor. Ama dikkat et, tapınak muhafızları onu koruyor! *şarkı söylemeye başlar*',
        options: [
          { text: 'Gidiyorum.' }
        ]
      }
    ]
  }
];

// Predefined NPCs for Otağ Köyü (Hub Village)
export const OTAG_KOYU_NPCS: NPCConfig[] = [
  {
    id: 'kam_batur',
    name: 'Kam Batur',
    title: 'Köyün Şamanı',
    type: 'kam',
    position: { x: 0, y: 1024 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Kam Batur',
        text: 'Hoş geldin, genç savaşçı. Karanlık Orman\'dan gelen kötülükler köyümüzü tehdit ediyor. Sana bir görev vermek istiyorum.',
        options: [
          { text: 'Göreve hazırım.', nextDialogId: 'quest_intro' },
          { text: 'Önce biraz dinlenmeliyim.', nextDialogId: 'rest' },
          { text: 'Bu kötülükler nedir?', nextDialogId: 'lore' }
        ]
      },
      {
        id: 'quest_intro',
        speaker: 'Kam Batur',
        text: 'Körmösler ormanı sardı. Onların lideri Al-Körmös\'ü bulup yok etmelisin. Dikkatli ol, güçlüler.',
        options: [
          { text: 'Başarılı olacağım!', nextDialogId: 'accept' },
          { text: 'Daha fazla bilgi ver.', nextDialogId: 'lore' }
        ]
      },
      {
        id: 'lore',
        speaker: 'Kam Batur',
        text: 'Yeraltı tanrısı Erlik\'in hizmetkarları dünyamıza sızdı. Körmösler, Albastılar, Karabasanlar... Hepsi onun emrinde.',
        options: [
          { text: 'Anladım, savaşmaya hazırım.', nextDialogId: 'accept' }
        ]
      },
      {
        id: 'rest',
        speaker: 'Kam Batur',
        text: 'Elbette. Köyde güvendesin. Hazır olduğunda gel.',
        options: [
          { text: 'Teşekkürler.' }
        ]
      },
      {
        id: 'accept',
        speaker: 'Kam Batur',
        text: 'Ruhlar seninle olsun, genç Alp. Karanlık Orman\'a doğudan ulaşabilirsin.',
        options: [
          { text: 'Hoşça kal, Kam.' }
        ]
      }
    ]
  },
  {
    id: 'demirci_temir',
    name: 'Demirci Temir',
    title: 'Usta Demirci',
    type: 'demirci',
    position: { x: 800, y: 1360 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Demirci Temir',
        text: 'Hoş geldin! Silahını güçlendirmek ister misin? Demir parçaları ve ruh damlası getir, ekipmanını +15\'e kadar yükseltebilirim.',
        options: [
          { text: 'Dükkanı göster.' },
          { text: 'Ekipman güçlendir (C tuşu)', nextDialogId: 'craft_info' },
          { text: 'Malzeme fiyatları nedir?' , nextDialogId: 'prices' },
          { text: 'Belki sonra.' }
        ]
      },
      {
        id: 'craft_info',
        speaker: 'Demirci Temir',
        text: 'C tuşuna basarak zanaat menüsünü açabilirsin. Güçlendirme şansı yüksek seviyelerde düşer, dikkatli ol!',
        options: [
          { text: 'Anladım, teşekkürler.' }
        ]
      },
      {
        id: 'prices',
        speaker: 'Demirci Temir',
        text: 'Demir parçası düşmanlardan düşer. Ruh damlası ise güçlü yaratıklardan. Kam boyası enderdir!',
        options: [
          { text: 'Teşekkürler.' }
        ]
      }
    ]
  },
  {
    id: 'tacir_altin',
    name: 'Tüccar Altın',
    title: 'Gezgin Tüccar',
    type: 'tacir',
    position: { x: 928, y: 1296 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Tüccar Altın',
        text: 'Selam savaşçı! İksirler, tılsımlar, nadir eşyalar... Her şeyim var! Altın göster, mal göstereyim.',
        options: [
          { text: 'Dükkanı göster.' },
          { text: 'Ne satıyorsun?', nextDialogId: 'info' },
          { text: 'Belki sonra.' }
        ]
      },
      {
        id: 'info',
        speaker: 'Tüccar Altın',
        text: 'Sağlık iksirleri, mana iksirleri, güç tılsımları satıyorum. Fiyatlar uygun!',
        options: [
          { text: 'Dükkanı göster.' },
          { text: 'Belki sonra.' }
        ]
      }
    ]
  },
  {
    id: 'ihtiyar_bilge',
    name: 'Bilge Koca',
    title: 'Köy İhtiyarı',
    type: 'ihtiyar',
    position: { x: -512, y: 512 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Bilge Koca',
        text: 'Ah, genç bir Alp... Eski zamanlarda Tengrinin ışığı tüm dünyayı aydınlatırdı. Şimdi ise karanlık güçler yükseliyor.',
        options: [
          { text: 'Tengri kimdir?', nextDialogId: 'tengri' },
          { text: 'Erlik hakkında ne biliyorsun?', nextDialogId: 'erlik' },
          { text: 'Savaş taktikleri ver.', nextDialogId: 'tips' },
          { text: 'Hoşça kal, bilge.' }
        ]
      },
      {
        id: 'tengri',
        speaker: 'Bilge Koca',
        text: 'Tengri, Gök Tanrısıdır. Tüm yaratılışın kaynağı. Işığın ve düzenin koruyucusu. Kam\'larımız ona dua eder.',
        options: [
          { text: 'Erlik kimdir?', nextDialogId: 'erlik' },
          { text: 'Teşekkürler.' }
        ]
      },
      {
        id: 'erlik',
        speaker: 'Bilge Koca',
        text: 'Erlik, yeraltının karanlık tanrısıdır. Körmösler, Albastılar onun yaratıklarıdır. Ruhları çalar, lanetler yağdırır.',
        options: [
          { text: 'Onu nasıl yenerim?', nextDialogId: 'tips' },
          { text: 'Korkunç.' }
        ]
      },
      {
        id: 'tips',
        speaker: 'Bilge Koca',
        text: 'Dinle genç savaşçı: Körmöslerin gözleri zayıftır, arkadan saldır. Albastılar hızlıdır ama dayanıksızdır. Karabasanlardan uzak dur, menzillidirler.',
        options: [
          { text: 'Değerli bilgiler, teşekkürler!' }
        ]
      }
    ]
  },
  {
    id: 'muhafiz_koruyucu',
    name: 'Muhafız Börü',
    title: 'Köy Koruyucusu',
    type: 'muhafiz',
    position: { x: -1120, y: 944 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Muhafız Börü',
        text: 'Dur yolcu! Ah, sen bir Alp\'sın. Köyümüze hoş geldin. Burası güvenli, ama dışarısı tehlikeli.',
        options: [
          { text: 'Dışarıda ne var?', nextDialogId: 'zones' },
          { text: 'Kontrol tuşları nedir?', nextDialogId: 'controls' },
          { text: 'Tamam, geçebilir miyim?' }
        ]
      },
      {
        id: 'zones',
        speaker: 'Muhafız Börü',
        text: 'Doğuda Karanlık Orman var - seviye 1-5 için uygun. Daha ötede Ruh Bataklığı ve Erlik Tapınağı var. Dikkatli ol!',
        options: [
          { text: 'Anladım, teşekkürler.' }
        ]
      },
      {
        id: 'controls',
        speaker: 'Muhafız Börü',
        text: 'WASD ile hareket et. Sol tık saldırı, sağ tık beceri. 1-5 tuşları beceriler için. I envanter, C zanaat, TAB harita.',
        options: [
          { text: 'Teşekkürler!' }
        ]
      }
    ]
  },
  {
    id: 'otaci_yesil',
    name: 'Otacı Yeşil',
    title: 'Şifalı Otacı',
    type: 'otaci',
    position: { x: 128, y: 1536 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Otacı Yeşil',
        text: 'Hoş geldin, yolcu. Gölet kenarında yetiştirdiğim otlardan güçlü iksirler yapıyorum. Yaralarını sarabilirim.',
        options: [
          { text: 'Dükkanı göster.' },
          { text: 'Hangi otları kullanıyorsun?', nextDialogId: 'herbs' },
          { text: 'Belki sonra.' }
        ]
      },
      {
        id: 'herbs',
        speaker: 'Otacı Yeşil',
        text: 'Gölet kenarındaki nilüferler sağlık verir. Bataklıkta yetişen mor mantarlar mana toplar. Ama dikkat et, bazı otlar zehirlidir!',
        options: [
          { text: 'Dükkanı göster.' },
          { text: 'Teşekkürler.' }
        ]
      }
    ],
    shopItems: ['health_potion', 'mana_potion', 'revival_potion']
  },
  // --- New Quest-Giver NPCs for Act 1 Side Quests ---
  {
    id: 'korkut_ata',
    name: 'Korkut Ata',
    title: 'Köy Bilgesi',
    type: 'ihtiyar',
    position: { x: -320, y: 768 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Korkut Ata',
        text: 'Hoş geldin, genç savaşçı. Karanlık günler yaklaşıyor... Köyümüz senin gibi yiğitlere muhtaç.',
        options: [
          { text: 'Ne oldu?', nextDialogId: 'quest_info' },
          { text: 'Bir görevin var mı?', nextDialogId: 'quest_offer' },
          { text: 'Hoşça kal, bilge.' }
        ]
      },
      {
        id: 'quest_info',
        speaker: 'Korkut Ata',
        text: 'Ormanlardan gelen haberler iyi değil. Erlik\'in hizmetkârları her geçen gün güçleniyor.',
        options: [
          { text: 'Yardım edebilirim.', nextDialogId: 'quest_offer' },
          { text: 'Anladım.' }
        ]
      },
      {
        id: 'quest_offer',
        speaker: 'Korkut Ata',
        text: 'Genç savaşçı, ormana giden avcımızdan haber alamıyoruz. Onu bulup getirir misin?',
        options: [
          { text: 'Onu bulacağım!', nextDialogId: 'accept' },
          { text: 'Belki sonra.' }
        ]
      },
      {
        id: 'accept',
        speaker: 'Korkut Ata',
        text: 'Ruhlar seninle olsun. Karanlık Orman\'ın derinliklerinde olmalı. Dikkatli ol, kurtlar tehlikeli.',
        options: [
          { text: 'Yolda.' }
        ]
      }
    ]
  },
  {
    id: 'kam_ana',
    name: 'Kam Ana',
    title: 'Şifacı Şaman',
    type: 'kam',
    position: { x: -640, y: 1280 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Kam Ana',
        text: 'Ruhlar seninle konuşuyor, savaşçı. Sende büyük bir güç var.',
        options: [
          { text: 'Yardıma ihtiyacın var mı?', nextDialogId: 'quest_offer' },
          { text: 'İksir yapabilir misin?', nextDialogId: 'craft' },
          { text: 'Hoşça kal.' }
        ]
      },
      {
        id: 'quest_offer',
        speaker: 'Kam Ana',
        text: 'Bataklıktaki sular zehirlenmiş. Bu Albastı\'ların işi... Onları temizle.',
        options: [
          { text: 'Temizleyeceğim!', nextDialogId: 'accept' },
          { text: 'Belki sonra.' }
        ]
      },
      {
        id: 'craft',
        speaker: 'Kam Ana',
        text: 'İksirlerimi dene. Savaşta işine yarayacaklardır. Ama önce bataklıktaki sorunu çözmemiz lazım.',
        options: [
          { text: 'Bataklıkta ne oluyor?', nextDialogId: 'quest_offer' },
          { text: 'Anladım.' }
        ]
      },
      {
        id: 'accept',
        speaker: 'Kam Ana',
        text: 'Tengri seni korusun. Albastılar bataklığın her yerinde. Beşini temizlersen su kaynakları düzelir.',
        options: [
          { text: 'Hemen gidiyorum.' }
        ]
      }
    ]
  },
  {
    id: 'alp_turan',
    name: 'Alp Turan',
    title: 'Savaş Ustası',
    type: 'muhafiz',
    position: { x: 480, y: 640 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Alp Turan',
        text: 'Savaşmayı öğrenmek mi istiyorsun? Önce gücünü göster!',
        options: [
          { text: 'Bir görevin var mı?', nextDialogId: 'quest_offer' },
          { text: 'Eğitim almak istiyorum.', nextDialogId: 'train' },
          { text: 'Hoşça kal.' }
        ]
      },
      {
        id: 'quest_offer',
        speaker: 'Alp Turan',
        text: 'Tapınağın yakınında ölüler uyanıyor. Git ve onları tekrar toprağa gönder!',
        options: [
          { text: 'Gideceğim!', nextDialogId: 'accept' },
          { text: 'Belki sonra.' }
        ]
      },
      {
        id: 'train',
        speaker: 'Alp Turan',
        text: 'Yeteneklerini geliştirmen gerekiyor. Pratik yap, güçlen. Mezarlıktaki ruhlarla savaşmak iyi bir antrenman olur.',
        options: [
          { text: 'Mezarlık görevi nedir?', nextDialogId: 'quest_offer' },
          { text: 'Anladım.' }
        ]
      },
      {
        id: 'accept',
        speaker: 'Alp Turan',
        text: 'Erlik Tapınağı\'na git, 15 huzursuz ruhu yok et. Bunu başarırsan sana bir beceri puanı vereceğim.',
        options: [
          { text: 'Başarılı olacağım!' }
        ]
      }
    ]
  },
];

// NPCs for Karanlık Orman (Dark Forest) - Act 1
export const KARANLIK_ORMAN_NPCS: NPCConfig[] = [
  {
    id: 'orman_izcisi',
    name: 'Orman İzcisi',
    title: 'Kayıp Avcı',
    type: 'muhafiz',
    position: { x: -320, y: 480 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Orman İzcisi',
        text: 'Dur! Daha ileri gitme. Bu orman normal değil... Ağaçlar hareket ediyor, gölgeler canlı. Köyden çıkalı üç gün oldu, geri dönüş yolunu bulamıyorum.',
        options: [
          { text: 'Ne oldu burada?', nextDialogId: 'story' },
          { text: 'Yardıma ihtiyacın var mı?', nextDialogId: 'quest' }
        ]
      },
      {
        id: 'story',
        speaker: 'Orman İzcisi',
        text: 'Orman ruhu Albız delirdi. Ağaçlara karanlık enerji yayıyor. Dikkat et, gece olunca çok daha tehlikeli oluyor.',
        options: [
          { text: 'Yardıma ihtiyacın var mı?', nextDialogId: 'quest' }
        ]
      },
      {
        id: 'quest',
        speaker: 'Orman İzcisi',
        text: 'Doğudaki açıklıkta eski bir sunak var. Oradaki karanlık kristalleri kırabilirsen ormandaki lanet zayıflayabilir. 5 kristal yeter.',
        options: [
          { text: 'Kristalleri bulacağım.', nextDialogId: 'accept' },
          { text: 'Belki sonra.' }
        ]
      },
      {
        id: 'accept',
        speaker: 'Orman İzcisi',
        text: 'Tengri korusun seni. Kristaller mor ışık yayar, kaçırmazsın.',
        options: [{ text: 'Yolda.' }]
      }
    ]
  }
];

// NPCs for Erlik Tapınağı (Erlik Temple) - Act 1 Boss
export const ERLIK_TAPINAGI_NPCS: NPCConfig[] = [
  {
    id: 'tapınak_ruhu',
    name: 'Tapınak Ruhu',
    title: 'Eski Muhafız',
    type: 'ihtiyar',
    position: { x: -128, y: 384 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Tapınak Ruhu',
        text: '*soluk bir ışıkla belirerek* Bu tapınak bir zamanlar Tengri\'ye adanmıştı... Şimdi Erlik\'in karanlığı her yere sızmış.',
        options: [
          { text: 'Bana yardım edebilir misin?', nextDialogId: 'help' },
          { text: 'Boss hakkında ipucu ver.', nextDialogId: 'boss_hint' }
        ]
      },
      {
        id: 'help',
        speaker: 'Tapınak Ruhu',
        text: 'Gücüm azaldı ama sana bir koruma verebilirim. Al, bu tılsım kısa süreliğine karanlık hasarı azaltacak.',
        options: [{ text: 'Teşekkürler, ruh.' }]
      },
      {
        id: 'boss_hint',
        speaker: 'Tapınak Ruhu',
        text: 'Tepegöz güçlüdür ama yavaştır. Saldırılarından kaç, arkasına dön. Gözü en zayıf noktası. Lav göllerinden uzak dur!',
        options: [{ text: 'Anlıyorum.' }]
      }
    ]
  }
];

// NPCs for Ergenekon Girişi (Ergenekon Entrance) - Act 2
export const ERGENEKON_GIRISI_NPCS: NPCConfig[] = [
  {
    id: 'dag_rehberi',
    name: 'Dağ Rehberi',
    title: 'Göçebe Savaşçı',
    type: 'muhafiz',
    position: { x: -256, y: 512 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Dağ Rehberi',
        text: 'Ergenekon... Efsanevi vadi. Atalarımız buradan çıktı, ama şimdi içeri girmek ölüm demek. Dağ geçitleri yaratıklarla dolu.',
        options: [
          { text: 'Nasıl geçebilirim?', nextDialogId: 'advice' },
          { text: 'Buralarda ne satıyorsun?', nextDialogId: 'trade' }
        ]
      },
      {
        id: 'advice',
        speaker: 'Dağ Rehberi',
        text: 'Fırtına Ovası\'na dikkat et. Yıldırım düşer, pek çok savaşçı orada hayatını kaybetti. Yerden yükselen tozu gördüğünde koş!',
        options: [{ text: 'Teşekkürler.' }]
      },
      {
        id: 'trade',
        speaker: 'Dağ Rehberi',
        text: 'İksirlerim ve güçlendirme taşlarım var. Yolculuk uzun, hazırlıklı ol.',
        options: [{ text: 'Bakalım.' }]
      }
    ],
    shopItems: ['health_potion', 'mana_potion']
  }
];

// NPCs for Fırtına Ovası (Storm Steppe) - Act 2
export const FIRTINA_OVASI_NPCS: NPCConfig[] = [
  {
    id: 'gocebe_kam',
    name: 'Göçebe Kam',
    title: 'Fırtına Şamanı',
    type: 'kam',
    position: { x: 0, y: 640 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Göçebe Kam',
        text: '*fırtına rüzgarında bağırarak* Bu fırtına doğal değil! Bir varlık ovayı kontrol ediyor. Fırtına Devleri... Onları durdurmak gerek!',
        options: [
          { text: 'Nasıl yardım edebilirim?', nextDialogId: 'quest' },
          { text: 'Fırtına Devleri nedir?', nextDialogId: 'lore' }
        ]
      },
      {
        id: 'lore',
        speaker: 'Göçebe Kam',
        text: 'Yer-Sub\'un hizmetkarları. Bir zamanlar doğayı korurdular, şimdi Erlik\'in emrindeler. Yıldırım güçleri var, ateş dirençli olman iyi olur.',
        options: [
          { text: 'Nasıl yardım edebilirim?', nextDialogId: 'quest' }
        ]
      },
      {
        id: 'quest',
        speaker: 'Göçebe Kam',
        text: 'Ovadaki 3 fırtına sütununu yık. Her biri devlerin gücünü artırıyor. Sütunları kırınca boss zayıflayacak.',
        options: [
          { text: 'Sütunları yıkacağım!', nextDialogId: 'accept' },
          { text: 'Belki sonra.' }
        ]
      },
      {
        id: 'accept',
        speaker: 'Göçebe Kam',
        text: 'Gök Tengri seninle olsun! Sütunlar elektrik yayar, yaklaşırken dikkatli ol.',
        options: [{ text: 'Gidiyorum.' }]
      }
    ]
  }
];

// NPCs for Ateş Mağarası (Fire Cave) - Act 2
export const ATES_MAGARASI_NPCS: NPCConfig[] = [
  {
    id: 'madenci_usta',
    name: 'Madenci Usta',
    title: 'Son Demirci',
    type: 'demirci',
    position: { x: -192, y: 448 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Madenci Usta',
        text: '*terini silerek* Bu mağaranın derinlerinde efsanevi cevher var. Ama ateş elementalleri her yere yayıldı. Ekibim kaçtı, ben kaldım.',
        options: [
          { text: 'Ekipmanımı güçlendirebilir misin?', nextDialogId: 'craft' },
          { text: 'Ateş elementalleri hakkında ne biliyorsun?', nextDialogId: 'lore' }
        ]
      },
      {
        id: 'craft',
        speaker: 'Madenci Usta',
        text: 'Tabii! Malzeme getir, en iyi işi yaparım. Buradaki ateş cevheri ile silahına ateş hasarı ekleyebilirim.',
        options: [{ text: 'İlginç, teşekkürler.' }]
      },
      {
        id: 'lore',
        speaker: 'Madenci Usta',
        text: 'Ateş Lordu mağaranın kalbinde yaşıyor. Su büyüleri etkili. Lav göllerinden uzak dur, düşersen kurtuluş yok.',
        options: [{ text: 'Dikkat edeceğim.' }]
      }
    ]
  }
];

// NPCs for Yer-Sub Tahtı (Yer-Sub Throne) - Act 2 Boss
export const YERSUB_TAHTI_NPCS: NPCConfig[] = [
  {
    id: 'yer_sub_habercisi',
    name: 'Yer-Sub Habercisi',
    title: 'Doğa Ruhu',
    type: 'otaci',
    position: { x: -64, y: 320 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Yer-Sub Habercisi',
        text: '*doğadan yükselen sesle* Durduğun yer kutsaldır. Yer-Sub\'un tahtı burada, ama o artık kendisi değil. Erlik\'in zehiri aklını çeldi.',
        options: [
          { text: 'Yer-Sub\'u kurtarabilir miyim?', nextDialogId: 'boss_hint' },
          { text: 'İyileşme iksiri var mı?', nextDialogId: 'heal' }
        ]
      },
      {
        id: 'boss_hint',
        speaker: 'Yer-Sub Habercisi',
        text: 'Yer-Sub toprak ve bitki gücü kullanır. Kökler yerden çıkacak, dikkatli ol. Zehir saldırılarından sonra temiz alana koş. Onu yenmek değil, arındırmak gerek.',
        options: [{ text: 'Anladım, hazırlanacağım.' }]
      },
      {
        id: 'heal',
        speaker: 'Yer-Sub Habercisi',
        text: '*yapraklar parlar* Al, bu doğanın hediyesi. Savaşta sana güç verecek.',
        options: [{ text: 'Teşekkürler.' }]
      }
    ]
  }
];

// NPCs for Karanlık Tünel (Dark Tunnel) - Act 3
export const KARANLIK_TUNEL_NPCS: NPCConfig[] = [
  {
    id: 'kayip_kasiif',
    name: 'Kayıp Kaşif',
    title: 'Yeraltı Gezgini',
    type: 'ihtiyar',
    position: { x: -384, y: 576 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Kayıp Kaşif',
        text: '*fenerini kaldırarak* Oh! Bir başka canlı! Tünellerde günlerdir yalnızım. Gölge yaratıkları her yerde. Haritam var ama karanlıkta işe yaramıyor.',
        options: [
          { text: 'Çıkış yolu biliyor musun?', nextDialogId: 'exit' },
          { text: 'Gölge yaratıkları nedir?', nextDialogId: 'lore' }
        ]
      },
      {
        id: 'exit',
        speaker: 'Kayıp Kaşif',
        text: 'Doğuya git, ama dikkat et! Ölüm Nehri\'ne çıkarsın. Tünelin sonundaki köprüyü geç, ama köprü bekçisi var... Kemik Savaşçı.',
        options: [{ text: 'Teşekkürler.' }]
      },
      {
        id: 'lore',
        speaker: 'Kayıp Kaşif',
        text: 'Erlik\'in yeraltı ordusu. Yaşayanların ruhlarını çalan gölgeler. Işık büyüleri çok etkili. Karanlıkta daha güçlüler.',
        options: [{ text: 'İpucu için teşekkürler.' }]
      }
    ]
  }
];

// NPCs for Ölüm Nehri (Death River) - Act 3
export const OLUM_NEHRI_NPCS: NPCConfig[] = [
  {
    id: 'ruh_kayikcisi',
    name: 'Ruh Kayıkçısı',
    title: 'Ölüler Taşıyıcı',
    type: 'kam',
    position: { x: 128, y: 704 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Ruh Kayıkçısı',
        text: '*kayığında süzülerek* Yaşayan biri... Nadir. Bu nehir ölülerin son yolculuğu. Ama sen henüz ölmedin... ilginç.',
        options: [
          { text: 'Karşıya geçmem lazım.', nextDialogId: 'cross' },
          { text: 'Bu nehir nedir?', nextDialogId: 'lore' }
        ]
      },
      {
        id: 'cross',
        speaker: 'Ruh Kayıkçısı',
        text: 'Bedava taşımam. Ama cesaretini sevdim. Nehirdeki kayıp ruhları arındırırsan seni karşıya geçiririm. 10 ruh yeterli.',
        options: [
          { text: 'Ruhları arındıracağım.', nextDialogId: 'accept' },
          { text: 'Yüzerek geçerim.' }
        ]
      },
      {
        id: 'lore',
        speaker: 'Ruh Kayıkçısı',
        text: 'Erlik\'in nehri. Ölenlerin ruhları burada akıyor. Ama Erlik\'in gücü arttıkça ruhlar huzur bulamıyor, canavar oluyorlar.',
        options: [{ text: 'Korkutucu.' }]
      },
      {
        id: 'accept',
        speaker: 'Ruh Kayıkçısı',
        text: 'Huzursuz ruhlar mavi ışık yayar. Onları savaşarak arındır. Seni bekliyor olacağım.',
        options: [{ text: 'Anlaştık.' }]
      }
    ]
  }
];

// NPCs for Kemik Ormanı (Bone Forest) - Act 3
export const KEMIK_ORMANI_NPCS: NPCConfig[] = [
  {
    id: 'kemik_kam',
    name: 'Kemik Kam',
    title: 'Lanetli Şaman',
    type: 'kam',
    position: { x: -448, y: 640 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Kemik Kam',
        text: '*kemik kolye şıngırdayarak* Yaşayan bir beden... Ne taze! Korkma, sana zarar vermeyeceğim. Bu ormanda başka düşmanların var.',
        options: [
          { text: 'Burada ne yapıyorsun?', nextDialogId: 'story' },
          { text: 'Tehlikeler hakkında bilgi ver.', nextDialogId: 'danger' }
        ]
      },
      {
        id: 'story',
        speaker: 'Kemik Kam',
        text: 'Bir zamanlar güçlü bir Kam\'dım. Erlik beni lanetledi ama zihnimi alamadı. Şimdi ne ölü ne diri... Bu ormanın bilgisini topluyorum.',
        options: [
          { text: 'Tehlikeler hakkında bilgi ver.', nextDialogId: 'danger' }
        ]
      },
      {
        id: 'danger',
        speaker: 'Kemik Kam',
        text: 'Kemik Golemler en tehlikelisi. Parçalanınca kendilerini yeniden birleştirir. Ateş büyüsü ile kemikleri yakmalısın, yoksa sürekli geri gelirler.',
        options: [{ text: 'Ateş büyüsü... Not aldım.' }]
      }
    ]
  }
];

// NPCs for Lanetli Saray (Cursed Palace) - Act 3 Boss
export const LANETLI_SARAY_NPCS: NPCConfig[] = [
  {
    id: 'saray_hizmetkari',
    name: 'Saray Hizmetkarı',
    title: 'Hayalet Uşak',
    type: 'ihtiyar',
    position: { x: -96, y: 384 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Saray Hizmetkarı',
        text: '*eğilerek* Hoş geldiniz efendim... Bu saray bir zamanlar görkemli bir hanlıktı. Şimdi lanet altında. Efendimiz Erlik\'in kuklası oldu.',
        options: [
          { text: 'Erlik Han hakkında ne biliyorsun?', nextDialogId: 'boss_hint' },
          { text: 'Saraydan nasıl geçerim?', nextDialogId: 'map' }
        ]
      },
      {
        id: 'boss_hint',
        speaker: 'Saray Hizmetkarı',
        text: 'Taht salonundaki Karabasan Lordu çok güçlü. Karanlık büyüler kullanır. Koridor tuzaklarına dikkat et. Avizeler düşebilir!',
        options: [{ text: 'Dikkatli olacağım.' }]
      },
      {
        id: 'map',
        speaker: 'Saray Hizmetkarı',
        text: 'Batı kanadından gidin, kütüphaneyi geçin, taht salonuna ulaşırsınız. Ama tuzaklarla dolu... Yavaş ve dikkatli ilerleyin.',
        options: [{ text: 'Teşekkürler.' }]
      }
    ]
  }
];

// NPCs for Erlik Kapısı (Erlik's Gate) - Act 4
export const ERLIK_KAPISI_NPCS: NPCConfig[] = [
  {
    id: 'kapi_muhafizi',
    name: 'Kapı Muhafızı',
    title: 'Eski Alp Savaşçı',
    type: 'muhafiz',
    position: { x: -192, y: 512 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Kapı Muhafızı',
        text: '*zırhı paslanmış, ama duruşu kararlı* Durduğun yer cehennemin kapısı. Buradan ötesi geri dönüşü olmayan yol. Hazır mısın?',
        options: [
          { text: 'Hazırım. İçeride ne var?', nextDialogId: 'info' },
          { text: 'Sen burada ne yapıyorsun?', nextDialogId: 'story' }
        ]
      },
      {
        id: 'info',
        speaker: 'Kapı Muhafızı',
        text: 'Cehennem Bahçesi... Erlik\'in en güçlü yaratıkları orada. Sonra Taht Odası. Erlik Han\'ın kendisi. Son savaşına hazırlan, geri dönüş yok.',
        options: [{ text: 'Korkmuyorum.' }]
      },
      {
        id: 'story',
        speaker: 'Kapı Muhafızı',
        text: 'Yıllar önce ben de senin gibi savaşçıydım. Erlik\'i durdurmaya geldim... Başaramadım. Ruhum burada hapis. Belki sen başarırsın.',
        options: [{ text: 'Başaracağım, sözüm olsun.' }]
      }
    ]
  }
];

// NPCs for Cehennem Bahçesi (Hell Garden) - Act 4
export const CEHENNEM_BAHCESI_NPCS: NPCConfig[] = [
  {
    id: 'dusen_melek',
    name: 'Düşen Melek',
    title: 'Terkedilmiş Ruh',
    type: 'otaci',
    position: { x: 64, y: 576 },
    dialogs: [
      {
        id: 'greeting',
        speaker: 'Düşen Melek',
        text: '*kanatları kırılmış, ışığı solgun* Bir mortal... burada? Erlik\'in bahçesinde hayatta kalmak büyük cesaret. Son iksirlerimi sana veririm.',
        options: [
          { text: 'Erlik Han hakkında bilgi ver.', nextDialogId: 'erlik' },
          { text: 'Bana iyileşme ver.', nextDialogId: 'heal' }
        ]
      },
      {
        id: 'erlik',
        speaker: 'Düşen Melek',
        text: 'Erlik Han üç form kullanır. İlk formda fiziksel saldırılar, ikincisinde karanlık büyüler, üçüncüsünde her ikisi birden. Üçüncü formda hızlı ol, acıma yok.',
        options: [{ text: 'Üç form... Anladım.' }]
      },
      {
        id: 'heal',
        speaker: 'Düşen Melek',
        text: '*ışık parlar* Al, bu son gücüm. İyi kullan savaşçı. Erlik\'i yenmek tüm ruhları özgürleştirecek.',
        options: [{ text: 'Teşekkürler, unutmayacağım.' }]
      }
    ]
  }
];
